/**
 * Hook to get filtered menu items based on user roles
 *
 * This hook provides a centralized way to get menu items that are visible
 * to the current user based on their roles.
 */

import { useMemo } from 'react';
import { authService, AuthUser } from '../auth/authService';
import {
  filterMenuItemsByRole,
  ALL_MENU_ITEMS,
  applyAdminNavPathSwap,
  menuItemToNavigationMenuItem,
  NavigationMenuItem,
} from '../utils/permissions';
import { getFirstAccessibleAnalyticsPath } from '../utils/analyticsConfig';
import { useEffect, useState } from 'react';

function authUserEmbeddedFacilityChanged(prev: AuthUser | null, next: AuthUser | null): boolean {
  return JSON.stringify(prev?.facility ?? null) !== JSON.stringify(next?.facility ?? null);
}

/**
 * Hook to get filtered menu items for NavigationSidebar
 * Returns menu items in the format expected by NavigationSidebar
 */
export function useMenuItems(): NavigationMenuItem[] {
  const [user, setUser] = useState<AuthUser | null>(null);

  // Get user from localStorage on mount and when it changes
  useEffect(() => {
    const checkUser = () => {
      try {
        const currentUser = authService.getUser();
        // Only update state if user actually changed (compare by ID to avoid unnecessary re-renders)
        setUser(prevUser => {
          const prevUserId = prevUser?.id;
          const currentUserId = currentUser?.id;

          // If IDs are different, or one is null/undefined, update
          if (prevUserId !== currentUserId) {
            return currentUser;
          }

          if (authUserEmbeddedFacilityChanged(prevUser, currentUser)) {
            return currentUser;
          }

          return prevUser;
        });
      } catch (error) {
        console.error('Error reading user_data:', error);
        setUser(prevUser => (prevUser === null ? null : null)); // Only update if not already null
      }
    };

    // Check immediately
    checkUser();

    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = () => {
      checkUser();
    };

    // Listen for custom event (when user logs in/out in same tab)
    const handleUserDataChanged = (event: CustomEvent<{ user: AuthUser | null }>) => {
      const { user: eventUser } = event.detail;
      setUser(prevUser => {
        const prevUserId = prevUser?.id;
        const currentUserId = eventUser?.id;

        // If IDs are different, or one is null/undefined, update
        if (prevUserId !== currentUserId) {
          return eventUser;
        }

        if (eventUser && authUserEmbeddedFacilityChanged(prevUser, eventUser)) {
          return eventUser;
        }

        return prevUser;
      });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataChanged', handleUserDataChanged as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataChanged', handleUserDataChanged as EventListener);
    };
  }, []);

  // Filter menu items based on user roles
  const menuItems = useMemo(() => {
    const visibleItems = applyAdminNavPathSwap(user, filterMenuItemsByRole(user, ALL_MENU_ITEMS));
    return visibleItems.map(item => {
      if (item.id === 'analytics') {
        return menuItemToNavigationMenuItem({
          ...item,
          path: getFirstAccessibleAnalyticsPath(user),
        });
      }
      return menuItemToNavigationMenuItem(item);
    });
  }, [user]);

  return menuItems;
}

/**
 * Hook to get the current user
 * Useful for components that need user info
 */
export function useAuthUser(): AuthUser | null {
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    const checkUser = () => {
      try {
        const currentUser = authService.getUser();
        // Only update state if user actually changed (compare by ID to avoid unnecessary re-renders)
        setUser(prevUser => {
          const prevUserId = prevUser?.id;
          const currentUserId = currentUser?.id;

          // If IDs are different, or one is null/undefined, update
          if (prevUserId !== currentUserId) {
            return currentUser;
          }

          if (authUserEmbeddedFacilityChanged(prevUser, currentUser)) {
            return currentUser;
          }

          return prevUser;
        });
      } catch (error) {
        console.error('Error reading user_data:', error);
        setUser(prevUser => (prevUser === null ? null : null)); // Only update if not already null
      }
    };

    checkUser();

    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = () => {
      checkUser();
    };

    // Listen for custom event (when user logs in/out in same tab)
    const handleUserDataChanged = (event: CustomEvent<{ user: AuthUser | null }>) => {
      const { user: eventUser } = event.detail;
      setUser(prevUser => {
        const prevUserId = prevUser?.id;
        const currentUserId = eventUser?.id;

        // If IDs are different, or one is null/undefined, update
        if (prevUserId !== currentUserId) {
          return eventUser;
        }

        if (eventUser && authUserEmbeddedFacilityChanged(prevUser, eventUser)) {
          return eventUser;
        }

        return prevUser;
      });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataChanged', handleUserDataChanged as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataChanged', handleUserDataChanged as EventListener);
    };
  }, []);

  return user;
}
